import { useEffect, useState } from 'react';
import { collection, doc, getDoc, onSnapshot, orderBy, query, where } from 'firebase/firestore';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import { signOut } from 'firebase/auth';
import { auth, db, functions, storage } from '../lib/firebase.js';
import { useAuth } from '../lib/auth.jsx';
import { registerForPush, unregisterPush } from '../lib/messaging.js';
import AITriageChat from '../components/AITriageChat.jsx';
import TicketChat, { useThreadUnread } from '../components/TicketChat.jsx';

export default function RequesterApp() {
  const { user, claims } = useAuth();
  const vendorId = claims?.vendor_id;

  const [vendorName, setVendorName] = useState('');
  // Task #106 — gate AI triage by the vendor-level add-on flag (not tier).
  // Tier prices stay decoupled from AI access.
  const [vendorAiAddonEnabled, setVendorAiAddonEnabled] = useState(false);
  // Task #122 — vendor admin can flip internalOnly to disable customer-facing
  // intake entirely. When ON, hide the SubmitForm and show a friendly notice.
  const [vendorInternalOnly, setVendorInternalOnly] = useState(false);
  const [skills, setSkills] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [error, setError] = useState(null);

  const assetIdFromUrl = new URLSearchParams(window.location.search).get('asset');
  const [asset, setAsset] = useState(null);

  // CHAT (2026-07-09 review finding #2): customers must register for push too,
  // or the tech's replies never reach a closed app — the whole point of the
  // in-app chat that replaced WhatsApp. Silent best-effort; 'default' permission
  // gets a one-tap banner below (Safari needs a user gesture anyway).
  const [pushHint, setPushHint] = useState(false);
  useEffect(() => {
    let mounted = true;
    (async () => {
      const token = await registerForPush();
      if (mounted && !token && typeof Notification !== 'undefined'
          && Notification.permission !== 'granted') {
        setPushHint(true);
      }
    })();
    return () => { mounted = false; unregisterPush(); };
  }, []);
  async function enablePush() {
    const token = await registerForPush();
    if (token) setPushHint(false);
  }

  useEffect(() => {
    if (!vendorId) return;
    (async () => {
      try {
        const v = await getDoc(doc(db, 'vendors', vendorId));
        if (v.exists()) {
          setVendorName(v.data().name || '');
          setVendorAiAddonEnabled(v.data().aiAddon?.enabled === true);
          setVendorInternalOnly(v.data().internalOnly === true);
        }
      } catch (e) { /* rule will bounce */ }
    })();

    const unsubSkills = onSnapshot(
      collection(db, 'vendors', vendorId, 'skillCategories'),
      (snap) => setSkills(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    );
    const unsubTickets = onSnapshot(
      query(
        collection(db, 'vendors', vendorId, 'tickets'),
        where('customerUid', '==', user.uid),
        orderBy('createdAt', 'desc'),
      ),
      (snap) => setTickets(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (err) => setError(err.message),
    );
    return () => { unsubSkills(); unsubTickets(); };
  }, [vendorId, user?.uid]);

  useEffect(() => {
    if (!vendorId || !assetIdFromUrl) return;
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'vendors', vendorId, 'assets', assetIdFromUrl));
        if (snap.exists()) setAsset({ id: snap.id, ...snap.data() });
        else setAsset(null);
      } catch (err) { /* silent */ }
    })();
  }, [vendorId, assetIdFromUrl]);

  return (
    <div className="requester">
      <header className="requester-header">
        <div className="brand-icon-row">
          <img
            src="/giraops-logo.png"
            alt="Giraops"
            className="brand-icon-img"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
          <div>
            <div className="brand-vendor">{vendorName || 'Giraops'}</div>
            <small style={{ color: 'var(--muted)' }}>Service request</small>
          </div>
        </div>
        <button onClick={() => signOut(auth)} className="btn-ghost">Sign out</button>
      </header>

      <main className="requester-main">
        {pushHint && (
          <div className="card" style={{
            padding: '10px 14px', marginBottom: 12, display: 'flex',
            alignItems: 'center', gap: 10, justifyContent: 'space-between', flexWrap: 'wrap',
            background: '#eff6ff', border: '1px solid #bfdbfe', fontSize: 13.5,
          }}>
            <span>🔔 Get notified when your technician replies or is on the way.</span>
            <button className="btn-primary" style={{ padding: '6px 14px', fontSize: 13 }} onClick={enablePush}>
              Enable notifications
            </button>
          </div>
        )}
        {vendorInternalOnly ? (
          <div className="card" style={{
            padding: 20,
            background: '#fef3c7',
            border: '1px solid #fcd34d',
            borderRadius: 8,
          }}>
            <h2 style={{ marginTop: 0, fontSize: 18 }}>This vendor uses Giraops internally</h2>
            <p style={{ marginBottom: 0, fontSize: 14, lineHeight: 1.5 }}>
              {vendorName || 'This service provider'} runs their own dispatch and
              doesn't accept service requests from the public app. Please contact
              them by your usual channel — phone, email, or in person — and they'll
              open the ticket for you on their side.
            </p>
            {tickets.length > 0 && (
              <p style={{ marginTop: 12, marginBottom: 0, fontSize: 13, color: 'var(--muted)' }}>
                Your past requests are still listed below.
              </p>
            )}
          </div>
        ) : (
          <SubmitForm
            vendorId={vendorId}
            userId={user.uid}
            skills={skills}
            asset={asset}
            aiAddonEnabled={vendorAiAddonEnabled}
          />
        )}

        <section style={{ marginTop: 32 }}>
          <h2 style={{ fontSize: 18 }}>Your recent requests</h2>
          {tickets.length === 0 ? (
            <div className="placeholder">You haven't submitted any requests yet.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {tickets.map((t) => (
                <TicketRow key={t.id} t={t} vendorId={vendorId} me={user} />
              ))}
            </div>
          )}
        </section>

        {error && <div className="error">{error}</div>}
      </main>
    </div>
  );
}

function SubmitForm({ vendorId, userId, skills, asset, aiAddonEnabled }) {
  const [skillId, setSkillId] = useState('');
  const [description, setDescription] = useState('');
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);

  // AI-PHASE-1: triage chat is shown when the tier enables it AND the selected
  // skill has a configured playbook. `triagePayload` holds the pre-chat state
  // captured at form-submit time so we can carry it forward into submitTicket.
  const [triagePayload, setTriagePayload] = useState(null);

  useEffect(() => {
    if (!asset || skillId) return;
    if (asset.defaultSkillCategoryId
        && skills.some((s) => s.id === asset.defaultSkillCategoryId)) {
      setSkillId(asset.defaultSkillCategoryId);
    }
  }, [asset, skills, skillId]);

  function pickFile(e) {
    const next = [...files, ...Array.from(e.target.files || [])].slice(0, 5);
    setFiles(next);
    e.target.value = '';
  }

  function removeFile(i) {
    setFiles(files.filter((_, idx) => idx !== i));
  }

  // Shared writer — submits the ticket optionally with the AI triage payload.
  async function actuallySubmitTicket({ skillCategoryId, descriptionFinal, photoUrls, aiTriage }) {
    await httpsCallable(functions, 'submitTicket')({
      skillCategoryId,
      description: descriptionFinal,
      photoUrls,
      ...(asset?.id ? { assetId: asset.id } : {}),
      ...(aiTriage ? { aiTriage } : {}),
    });
  }

  async function submit(e) {
    e.preventDefault();
    if (!skillId || !description.trim()) return;
    setBusy(true); setError(null);

    try {
      // Upload any selected photos to Cloud Storage first — both the AI chat
      // and the direct-submit path need real URLs to attach.
      const photoUrls = [];
      const tempId = `pending-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      for (const file of files) {
        const path = `vendors/${vendorId}/tickets/${tempId}/photos/${file.name}`;
        const ref = storageRef(storage, path);
        await uploadBytes(ref, file);
        const url = await getDownloadURL(ref);
        photoUrls.push(url);
      }

      // Decide whether to route through the AI triage chat or submit directly.
      // Task #106 — vendor-level add-on (not tier-coupled) gates this.
      // 2026-07-12 (Daniele): playbook requirement DROPPED — the AI is the default
      // first line of support for every skill when the add-on is on. The backend
      // already handles playbook-less skills (generic best-practice questioning +
      // hardcoded safety guardrails); a playbook just enriches it.
      const skill = skills.find((s) => s.id === skillId);
      const useAiTriage = aiAddonEnabled === true;

      if (useAiTriage) {
        // Hand control to the chat. Saving the payload triggers the render
        // switch below. Form fields stay set so "← Edit my request" works.
        setTriagePayload({
          skillId,
          skill: { id: skill.id, name: skill.name },
          description: description.trim(),
          photoUrls,
        });
        setBusy(false);
        return;
      }

      await actuallySubmitTicket({
        skillCategoryId: skillId,
        descriptionFinal: description.trim(),
        photoUrls,
      });

      setDone(true);
      setSkillId('');
      setDescription('');
      setFiles([]);
      setTimeout(() => setDone(false), 4000);
    } catch (err) {
      setError(err.message || 'Could not submit. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  // Called by AITriageChat when the customer (or AI) decides to escalate.
  // The triage callable already wrote the summary into the session doc, so we
  // just pass the sessionId — submitTicket reads + denormalizes it.
  async function onTriageEscalate(escalationResult) {
    setBusy(true); setError(null);
    try {
      await actuallySubmitTicket({
        skillCategoryId: triagePayload.skillId,
        descriptionFinal: triagePayload.description,
        photoUrls: triagePayload.photoUrls,
        aiTriage: { sessionId: escalationResult?.sessionId || null },
      });
      setDone(true);
      setSkillId('');
      setDescription('');
      setFiles([]);
      setTriagePayload(null);
      setTimeout(() => setDone(false), 4000);
    } catch (err) {
      setError(err.message || 'Could not dispatch. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  // Customer abandoned the chat — drop the session, restore the form.
  function onTriageBail() {
    setTriagePayload(null);
  }

  // Task #102 — customer confirmed the AI fix worked. Skip submitTicket; the
  // server-side aiTriageResolve already created a CLOSED ticket. Just show a
  // friendly confirmation in the existing "done" affordance, then clean up.
  function onTriageResolve() {
    setDone(true);
    setSkillId('');
    setDescription('');
    setFiles([]);
    setTriagePayload(null);
    setTimeout(() => setDone(false), 6000);
  }

  // Render either the AI triage chat or the form, depending on triagePayload.
  if (triagePayload) {
    return (
      <AITriageChat
        skill={triagePayload.skill}
        initialDescription={triagePayload.description}
        photoUrls={triagePayload.photoUrls}
        assetId={asset?.id}
        onEscalate={onTriageEscalate}
        onResolve={onTriageResolve}
        onBail={onTriageBail}
      />
    );
  }

  return (
    <form onSubmit={submit} className="card">
      <h2 style={{ marginTop: 0, fontSize: 18 }}>New service request</h2>

      {asset && (
        <div
          style={{
            background: '#fef3c7',
            border: '1px solid #fcd34d',
            borderLeft: '3px solid #f97316',
            borderRadius: 6,
            padding: '10px 12px',
            marginBottom: 16,
            fontSize: 14,
          }}
        >
          <strong>Reporting issue for:</strong> {asset.name}
          {asset.siteName ? ` at ${asset.siteName}` : ''}
        </div>
      )}

      <label>What's the issue with?</label>
      <select value={skillId} onChange={(e) => setSkillId(e.target.value)} required>
        <option value="">Choose a category...</option>
        {skills.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
      </select>

      <label style={{ marginTop: 12 }}>Describe what's happening</label>
      <textarea
        rows={5}
        placeholder="e.g. Walk-in freezer reading +5C since 11pm, frost on coils, compressor cycling. Stock at risk."
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        maxLength={2000}
        required
        style={{ width: '100%', padding: '10px 12px', border: '1px solid var(--border)', borderRadius: 'var(--radius)', fontFamily: 'inherit', fontSize: 15 }}
      />

      <label style={{ marginTop: 12 }}>Add photos (up to 5, optional)</label>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
        {files.map((f, i) => (
          <div key={i} className="file-chip">
            {f.name}
            <button type="button" onClick={() => removeFile(i)}>x</button>
          </div>
        ))}
      </div>
      {files.length < 5 && (
        <input type="file" accept="image/*" capture="environment" multiple onChange={pickFile} />
      )}

      <button type="submit" disabled={busy} className="btn-primary" style={{ marginTop: 16, width: '100%' }}>
        {busy ? 'Submitting...' : 'Submit request'}
      </button>

      {done && <div className="success">Request submitted. We're contacting your service team now.</div>}
      {error && <div className="error">{error}</div>}
    </form>
  );
}

function TicketRow({ t, vendorId, me }) {
  const isClosed = t.status === 'CLOSED';
  const alreadyRated = !!t.feedback?.customer;
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  // CHAT (spec-ticket-chat.md) — talk to the assigned tech inside the app.
  const [chatOpen, setChatOpen] = useState(false);
  const chatUnread = useThreadUnread(vendorId, t, me?.uid);
  const hasThread = !!t.assignedTo || !!t.chat?.lastMessageAt;

  return (
    <div className="card" style={{ padding: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <strong>{t.skillName}</strong>
        <StatusBadge status={t.status} assignedTo={t.assignedTo} />
      </div>
      <div style={{ marginTop: 4, color: 'var(--muted)', fontSize: 14 }}>
        {t.createdAt?.toDate?.().toLocaleString() || ''}
      </div>
      <div style={{ marginTop: 8, fontSize: 14 }}>
        {t.description.length > 140 ? t.description.slice(0, 140) + '...' : t.description}
      </div>
      {t.assignedTo && (
        <div style={{ marginTop: 8, fontSize: 14, color: 'var(--muted)' }}>
          Assigned to <strong>{t.assignedTo.name}</strong>
        </div>
      )}

      {hasThread && (
        <div style={{ marginTop: 10 }}>
          <button
            className={isClosed ? 'btn-ghost' : 'btn-primary'}
            style={{ width: '100%', position: 'relative' }}
            onClick={() => setChatOpen(true)}
          >
            💬 {isClosed ? 'View conversation' : `Chat with ${t.assignedTo?.name?.split(' ')[0] || 'the technician'}`}
            {chatUnread && (
              <span style={{
                position: 'absolute', top: 6, right: 8, width: 10, height: 10,
                borderRadius: '50%', background: '#dc2626',
              }} />
            )}
          </button>
        </div>
      )}
      {chatOpen && (
        <TicketChat
          vendorId={vendorId}
          ticket={t}
          me={{ uid: me.uid, name: t.customerName || me.displayName || 'Me', role: 'customer' }}
          onClose={() => setChatOpen(false)}
        />
      )}

      {isClosed && alreadyRated && (
        <div style={{
          marginTop: 10, padding: '8px 10px', background: '#f0fdf4',
          borderRadius: 6, fontSize: 13, color: '#166534',
        }}>
          You rated this <strong>{t.feedback.customer.rating}/5</strong>{'  '}
          <span style={{ color: '#f59e0b' }}>
            {'★'.repeat(t.feedback.customer.rating)}{'☆'.repeat(5 - t.feedback.customer.rating)}
          </span>
          {t.feedback.customer.comment && (
            <div style={{ marginTop: 4, fontStyle: 'italic' }}>
              "{t.feedback.customer.comment}"
            </div>
          )}
        </div>
      )}
      {isClosed && !alreadyRated && (
        <div style={{ marginTop: 10 }}>
          <button
            className="btn-primary"
            style={{ width: '100%' }}
            onClick={() => setFeedbackOpen(true)}
          >
            Rate this service
          </button>
        </div>
      )}

      {feedbackOpen && (
        <CustomerFeedbackModal
          ticket={t}
          onClose={() => setFeedbackOpen(false)}
        />
      )}
    </div>
  );
}

// ============================================================================
// CustomerFeedbackModal
// ============================================================================

function CustomerFeedbackModal({ ticket, onClose }) {
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [wouldRecommend, setWouldRecommend] = useState(null);
  const [productFeedback, setProductFeedback] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [done, setDone] = useState(false);

  async function submit() {
    setErr(null);
    if (rating < 1 || rating > 5) {
      setErr('Pick a star rating first.');
      return;
    }
    setBusy(true);
    try {
      await httpsCallable(functions, 'submitTicketFeedback')({
        ticketId: ticket.id,
        rating,
        comment: comment.trim(),
        wouldRecommend,
        productFeedback: productFeedback.trim() || undefined,
      });
      setDone(true);
    } catch (e) {
      setErr(prettyFeedbackError(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="ra-modal-backdrop" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="ra-modal" style={{ maxWidth: 480 }}>
        {done ? (
          <>
            <h3>Thank you</h3>
            <p>Your feedback was sent. {ticket.assignedTo?.name || 'The team'} will see it.</p>
            <div className="ra-modal-actions">
              <button className="btn-primary" onClick={onClose}>Close</button>
            </div>
          </>
        ) : (
          <>
            <h3>How was the service?</h3>
            <p className="settings-help" style={{ marginTop: 0 }}>
              {ticket.assignedTo?.name || 'A technician'} helped with your{' '}
              <strong>{ticket.skillName}</strong> request. Your rating goes to the administrator.
            </p>

            <div className="ra-field">
              <label>Your rating</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setRating(n)}
                    style={{
                      background: 'transparent', border: 'none', cursor: 'pointer',
                      padding: '2px 2px', lineHeight: 1, fontSize: 46,
                      color: n <= rating ? '#f59e0b' : '#94a3b8',
                      textShadow: n <= rating ? '0 1px 4px rgba(245,158,11,0.45)' : 'none',
                      transition: 'transform 0.1s ease',
                    }}
                    aria-label={`${n} star${n === 1 ? '' : 's'}`}
                  >
                    {n <= rating ? '★' : '☆'}
                  </button>
                ))}
                {rating > 0 && (
                  <span style={{ marginLeft: 6, fontSize: 15, fontWeight: 600, color: '#f59e0b' }}>
                    {rating}/5
                  </span>
                )}
              </div>
            </div>

            <div className="ra-field">
              <label htmlFor="fb-comment">Comment (optional)</label>
              <textarea
                id="fb-comment"
                rows={3}
                maxLength={1000}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                className="ra-input"
                placeholder="What worked? What could have been better?"
              />
            </div>

            <div className="ra-field">
              <label>Would you call this team again?</label>
              <div className="ra-pills">
                <button
                  type="button"
                  className={`ra-pill ${wouldRecommend === true ? 'is-active' : ''}`}
                  onClick={() => setWouldRecommend(wouldRecommend === true ? null : true)}
                >
                  Yes
                </button>
                <button
                  type="button"
                  className={`ra-pill ${wouldRecommend === false ? 'is-active' : ''}`}
                  onClick={() => setWouldRecommend(wouldRecommend === false ? null : false)}
                >
                  No
                </button>
              </div>
            </div>

            <div className="ra-field" style={{
              marginTop: 12, paddingTop: 12, borderTop: '1px solid #e5e7eb',
            }}>
              <label htmlFor="fb-product">
                Optional: feedback about Giraops itself
              </label>
              <p className="settings-help" style={{ marginTop: 0, fontSize: 12 }}>
                Anything to tell us (the app makers, not your vendor) about how Giraops
                worked for you? Bugs, missing features, confusing parts - we read every one.
              </p>
              <textarea
                id="fb-product"
                rows={2}
                maxLength={1000}
                value={productFeedback}
                onChange={(e) => setProductFeedback(e.target.value)}
                className="ra-input"
                placeholder="(Optional) Leave blank if you don't have anything to share."
              />
            </div>

            {err && <div className="error-banner" style={{ marginTop: 8 }}>{err}</div>}

            <div className="ra-modal-actions" style={{ marginTop: 14 }}>
              <button className="btn-ghost" onClick={onClose} disabled={busy}>Cancel</button>
              <button className="btn-primary" onClick={submit} disabled={busy || rating < 1}>
                {busy ? 'Sending...' : 'Submit feedback'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function prettyFeedbackError(e) {
  const m = e?.message || '';
  if (m.includes('already-exists')) return 'Feedback for this ticket has already been submitted.';
  if (m.includes('failed-precondition')) return 'Ticket is not closed yet - rate it after the service is complete.';
  if (m.includes('permission-denied')) return 'You can only rate your own tickets.';
  return m || 'Could not submit feedback.';
}

function StatusBadge({ status, assignedTo }) {
  let label = status;
  let color = '#64748b';
  if (status === 'NEW' || status === 'ROUTING') { label = 'Looking for technician...'; color = '#f59e0b'; }
  else if (status?.startsWith('RINGING')) { label = 'Calling team...'; color = '#f59e0b'; }
  else if (status?.startsWith('ESCALATED')) { label = 'Escalating...'; color = '#dc2626'; }
  else if (status === 'ASSIGNED' || status === 'ACCEPTED') { label = `Assigned to ${assignedTo?.name || 'tech'}`; color = '#10b981'; }
  else if (status === 'CLOSED') { label = 'Resolved'; color = '#10b981'; }

  return <span className="badge" style={{ background: 'transparent', border: `1px solid ${color}`, color }}>{label}</span>;
}
